import InnerBorderCard from "@components/InnerBorderCard";
import LArtwork from "@components/LArtwork";
import LBox from "@components/LBox";
import LGradient from "@components/LGradient";
import LPressable from "@components/LPressable";
import LText from "@components/LText";
import { getActiveTab } from "@hooks/useActiveTab";
import { theme } from "@theme";
import SkeletonPlaceholder from "react-native-skeleton-placeholder";
import { moderateScale as scale } from "@utils/scale";
import { Platform, useWindowDimensions } from "react-native";
import dayjs from "dayjs";
import { ExclusiveRibbon } from "@libs/shared-ui/native";
import { getViewPort } from "@utils/web/config";
import { router } from "expo-router";

const DEFAULT_SIZE = scale(120);

type CollectionTileProps = {
  images: (string | undefined | null)[] | null;
  size: number;
  title: string;
  artists: string;
  blurhash?: (string | undefined | null)[];
  showReleaseYear?: boolean;
  releaseYear: string | null;
  hasPrivateSong: boolean;
  privateSongIds?: string[];
};

type ArtistTileProps = {
  size: number;
  name: string;
  image?: string | null;
};

type LargeTileProps = {
  item?:
    | {
        type: "artist";
        id: string;
        name: string;
        image: string | null;
      }
    | {
        type: "album";
        id: string;
        title: string;
        artistDisplayText: string;
        image: string | null;
        releaseDate: string | null;
        hasPrivateSong: boolean;
        privateSongIds: string[];
      }
    | {
        type: "playlist";
        id: string;
        title: string;
        images: string[] | null;
        user: {
          name: string;
        };
        songs?: {
          id: string;
        }[];
        hasPrivateSong?: boolean;
        privateSongIds: string[];
      };
  size?: number;
  screen: "home" | "library" | "explore" | null;
  showReleaseYear?: boolean;
  showPlaylistOwner?: boolean;
};

const CollectionTile = ({
  images,
  size,
  title,
  artists,
  blurhash,
  showReleaseYear,
  releaseYear,
  hasPrivateSong,
}: CollectionTileProps) => {
  return (
    <LBox overflow="hidden" width={size}>
      <InnerBorderCard width={size} height={size} borderRadius="m">
        <LArtwork
          size={size - 2}
          url={images}
          imageSize="medium"
          borderRadius={"m"}
          blurhash={blurhash}
        />
        {/* VIP corner marks exclusive content (LIS-602) — the green stroke
            and padlock badge are gone. */}
        {hasPrivateSong && (
          <ExclusiveRibbon
            containerSize={size}
            variant={size >= 100 ? "featured" : "compact"}
          />
        )}
      </InnerBorderCard>
      {/* middle ellipsis: series titles differ only in their suffix
          ("… Podcast #31") — end-truncation erases the distinguishing part.
          react-native-web has no middle ellipsis, so web falls back to tail. */}
      <LText
        variant="bodyM2"
        textAlign="left"
        numberOfLines={1}
        ellipsizeMode="middle"
        mt="s1"
      >
        {title}
      </LText>
      <LText variant="bodyM" textAlign="left" numberOfLines={1} color="subText">
        {showReleaseYear ? releaseYear : artists}
      </LText>
    </LBox>
  );
};
const ArtistTile = ({ image, name }: Omit<ArtistTileProps, "size">) => {
  const window = useWindowDimensions();
  const height = Math.max(getViewPort(window).height / 4, 208);
  const width = 0.8 * height;
  const borderRadius = "l-xl";

  return (
    <InnerBorderCard width={width} height={height} borderRadius={borderRadius}>
      <LArtwork
        imageStyle={[
          {
            height: "100%",
            aspectRatio: width / height,
            borderRadius: theme.borderRadii["l-xl"],
          },
          // on web, we need to set the aspect ratio to fill the container
          Platform.OS === "web" ? { aspectRatio: width / height } : {},
        ]}
        url={image}
      />
      <LGradient
        position="absolute"
        bottom={0}
        width="100%"
        aspectRatio={width / height}
        colors={["transparent", "rgba(0,0,0,0.75)"]}
        locations={[0.7, 1]}
        height={height}
        borderBottomLeftRadius="l-xl"
        borderBottomRightRadius="l-xl"
      />
      <LBox
        position="absolute"
        bottom={0}
        width="100%"
        flex={1}
        padding="s3"
        flexDirection="row"
        alignItems="center"
        gap="s1_5"
      >
        <LArtwork size={20} url={image} borderRadius={borderRadius} />
        <LText variant="bodyM" numberOfLines={1} style={{ flex: 1 }}>
          {name}
        </LText>
      </LBox>
    </InnerBorderCard>
  );
};

const Skeleton = ({ size }: { size: number }) => (
  <LBox>
    <SkeletonPlaceholder
      highlightColor="rgba(255,255,255,1)"
      backgroundColor="rgba(255,255,255,0.3)"
      speed={1200}
    >
      <SkeletonPlaceholder.Item>
        <SkeletonPlaceholder.Item
          width={size}
          height={size}
          borderRadius={theme.borderRadii.m}
          marginBottom={theme.spacing.s3}
        />
        <SkeletonPlaceholder.Item
          width={size / 2}
          height={theme.textVariants.bodyM.lineHeight}
          borderRadius={theme.borderRadii.m}
          marginBottom={theme.spacing.s3}
        />
        <SkeletonPlaceholder.Item
          width={size * 0.8}
          height={theme.textVariants.bodyMSemiBold.lineHeight}
          borderRadius={theme.borderRadii.m}
        />
      </SkeletonPlaceholder.Item>
    </SkeletonPlaceholder>
  </LBox>
);

const LargeTile = ({
  item,
  size = DEFAULT_SIZE,
  screen,
  showReleaseYear,
  showPlaylistOwner = true,
}: LargeTileProps) => {
  if (!item) {
    return <Skeleton size={size} />;
  }
  const { type } = item;

  const getTitle = () => {
    switch (type) {
      case "artist":
        return item.name;
      case "album":
        return item.title;
      case "playlist":
        return item.title;
      default:
        return "";
    }
  };

  const getArtists = () => {
    switch (type) {
      case "album":
        return item.artistDisplayText;
      case "playlist":
        return showPlaylistOwner ? (item.user?.name ?? "") : "";
      case "artist":
        return "";
    }
  };

  const getReleaseYear = () => {
    switch (type) {
      case "album":
        return item.releaseDate
          ? dayjs(item.releaseDate).year().toString()
          : null;
      case "artist":
      case "playlist":
        return null;
    }
  };

  const getImages = () => {
    switch (type) {
      case "album":
        return [item.image];
      case "playlist":
        return item.images;
      case "artist":
        return [item.image];
      default:
        return [""];
    }
  };

  const getLink = () => {
    const activeTab = getActiveTab();
    switch (screen) {
      case "home":
        return `/(app)/(tabs)/${activeTab}/[collectionType]/[collectionId]` as const;
      case `library`:
        return `/(app)/(tabs)/${activeTab}/[collectionType]/[collectionId]` as const;
      case `explore`:
        return `/(app)/(tabs)/${activeTab}/[collectionType]/[collectionId]` as const;
      case null:
        return `/(app)/[collectionType]/[collectionId]` as const;
    }
  };

  const hasPrivateSong =
    item.type === "artist" ? false : (item.hasPrivateSong ?? false);

  return (
    <LPressable
      onPress={() => {
        router.navigate({
          pathname: getLink(),
          params: {
            collectionType: type,
            collectionId: item.id,
          },
        });
      }}
      sentryEventName={`large_tile_${type}_${item.id}`}
    >
      <LBox>
        {type !== "artist" ? (
          <CollectionTile
            artists={getArtists()}
            images={getImages()}
            size={size}
            title={getTitle()}
            showReleaseYear={showReleaseYear}
            releaseYear={getReleaseYear()}
            hasPrivateSong={hasPrivateSong}
            privateSongIds={item.privateSongIds}
          />
        ) : (
          <ArtistTile image={item.image} name={item.name} />
        )}
      </LBox>
    </LPressable>
  );
};

export default LargeTile;
